/**
 * Tutorial overlay geometry.
 *
 * The overlay is position:fixed in screen space, but everything it
 * points at lives inside `.app-canvas`, which is letterboxed: on a wide
 * or short viewport the canvas is smaller than the window and offset
 * from its origin. So the spotlight, the modal, and the arrow are all
 * computed against the canvas rect, never `window.innerWidth/Height`
 * (see "UI Scaling Architecture" in CLAUDE.md).
 *
 * Placement rules:
 *   - The spotlight is the padded target clipped to the canvas, so a
 *     target flush against the canvas edge keeps a visible ring.
 *   - The modal prefers the side of the target away from the canvas
 *     centre, but only when that side actually has room for it. A
 *     target that spans the full canvas width (the quest strip) has
 *     room on neither side; there the modal goes above or below,
 *     whichever side has more space, so it never covers its own target.
 *     A target that fills the canvas on both axes has no clean slot at
 *     all and keeps the clamped side placement, arrow included.
 */

export const SPOTLIGHT_PADDING = 10;
export const ARROW_LENGTH = 72;
export const ARROW_GAP = 18;
export const EDGE_MARGIN = 16;

// Half the arrow box height, matching the 2.25cqw the overlay renders.
const ARROW_HALF_THICKNESS = 18;
// Keeps the 2px spotlight stroke fully inside the canvas.
const SPOTLIGHT_EDGE_INSET = 2;

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.max(min, Math.min(value, max));
}

/** The visible canvas rect, in viewport space. Falls back to the window. */
export function readOverlayBounds() {
  const vw = (typeof window !== 'undefined' && window.innerWidth) || 1280;
  const vh = (typeof window !== 'undefined' && window.innerHeight) || 800;
  const fallback = { left: 0, top: 0, width: vw, height: vh };
  if (typeof document === 'undefined') return fallback;
  const canvas = document.querySelector('.app-canvas');
  if (!canvas) return fallback;
  const r = canvas.getBoundingClientRect();
  if (!r.width || !r.height) return fallback;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/**
 * @param {object} args
 * @param {{left:number,top:number,width:number,height:number}} args.bounds canvas rect
 * @param {{left:number,top:number,width:number,height:number}|null} args.targetRect
 * @param {number} args.modalWidth
 * @param {number} args.modalHeight
 * @returns {{anchor:string, spotlight:object|null, modalLeft:number, modalTop:number, arrow:object|null}}
 */
export function computeTutorialPlacement({ bounds, targetRect, modalWidth, modalHeight }) {
  const minLeft = bounds.left + EDGE_MARGIN;
  const maxLeft = bounds.left + bounds.width - modalWidth - EDGE_MARGIN;
  const minTop = bounds.top + EDGE_MARGIN;
  const maxTop = bounds.top + bounds.height - modalHeight - EDGE_MARGIN;

  const centered = () => ({
    anchor: 'center',
    spotlight: null,
    modalLeft: clamp(bounds.left + (bounds.width - modalWidth) / 2, minLeft, maxLeft),
    modalTop: clamp(bounds.top + (bounds.height - modalHeight) / 2, minTop, maxTop),
    arrow: null,
  });

  if (!targetRect) return centered();

  const innerLeft = bounds.left + SPOTLIGHT_EDGE_INSET;
  const innerTop = bounds.top + SPOTLIGHT_EDGE_INSET;
  const innerRight = bounds.left + bounds.width - SPOTLIGHT_EDGE_INSET;
  const innerBottom = bounds.top + bounds.height - SPOTLIGHT_EDGE_INSET;

  const x = clamp(targetRect.left - SPOTLIGHT_PADDING, innerLeft, innerRight);
  const y = clamp(targetRect.top - SPOTLIGHT_PADDING, innerTop, innerBottom);
  const spotlight = {
    x,
    y,
    width: clamp(targetRect.left + targetRect.width + SPOTLIGHT_PADDING, innerLeft, innerRight) - x,
    height: clamp(targetRect.top + targetRect.height + SPOTLIGHT_PADDING, innerTop, innerBottom) - y,
  };

  const spotRight = spotlight.x + spotlight.width;
  const spotBottom = spotlight.y + spotlight.height;
  const cx = spotlight.x + spotlight.width / 2;
  const cy = spotlight.y + spotlight.height / 2;
  const lane = ARROW_GAP + ARROW_LENGTH;
  const needSide = modalWidth + lane + EDGE_MARGIN;

  const roomRight = bounds.left + bounds.width - spotRight;
  const roomLeft = spotlight.x - bounds.left;
  const preferRight = cx < bounds.left + bounds.width / 2;

  const sidePlacement = (side) => ({
    anchor: side,
    spotlight,
    modalLeft: clamp(
      side === 'right' ? spotRight + lane : spotlight.x - lane - modalWidth,
      minLeft,
      maxLeft,
    ),
    modalTop: clamp(cy - modalHeight / 2, minTop, maxTop),
    arrow: {
      left: side === 'right' ? spotRight + ARROW_GAP : spotlight.x - lane,
      top: cy - ARROW_HALF_THICKNESS,
      rotate: side === 'right' ? 180 : 0,
    },
  });

  let anchor = null;
  if (preferRight && roomRight >= needSide) anchor = 'right';
  else if (!preferRight && roomLeft >= needSide) anchor = 'left';
  else if (roomRight >= needSide) anchor = 'right';
  else if (roomLeft >= needSide) anchor = 'left';

  if (anchor) return sidePlacement(anchor);

  // No horizontal room on either side: go above or below instead, so the
  // modal never lands on top of the element it is describing.
  const roomAbove = spotlight.y - bounds.top;
  const roomBelow = bounds.top + bounds.height - spotBottom;
  const needVertical = modalHeight + lane + EDGE_MARGIN;

  // A target that fills the canvas (the deck editor's collection panel,
  // the auction list) has room on no side at all. Neither placement is
  // clean, so keep the side one: clamped onto the wider margin, with the
  // arrow still pointing at the target. Dropping it to a vertical slot
  // would clamp it into the middle of the target and lose the arrow too.
  if (roomAbove < needVertical && roomBelow < needVertical) {
    return sidePlacement(roomRight >= roomLeft ? 'right' : 'left');
  }

  const above = roomAbove >= needVertical
    && (roomBelow < needVertical || roomAbove >= roomBelow);

  const modalTop = clamp(
    above ? spotlight.y - lane - modalHeight : spotBottom + lane,
    minTop,
    maxTop,
  );
  const modalLeft = clamp(cx - modalWidth / 2, minLeft, maxLeft);

  // Only draw the arrow when the modal actually cleared the target; a
  // clamped modal can end up flush against it, and an arrow there would
  // point at the modal's own edge.
  const gap = above ? spotlight.y - (modalTop + modalHeight) : modalTop - spotBottom;
  const arrow = gap >= ARROW_LENGTH
    ? {
        left: cx - ARROW_LENGTH / 2,
        top: (above ? spotlight.y - gap / 2 : spotBottom + gap / 2) - ARROW_HALF_THICKNESS,
        rotate: above ? 90 : 270,
      }
    : null;

  return { anchor: above ? 'above' : 'below', spotlight, modalLeft, modalTop, arrow };
}
